import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Insight } from "@/components/Insight";
import { HandRatioMeter } from "@/components/HandRatioMeter";
import {
  Mistake, TryThis, KeyIdea, Pull,
  Steps, Step, NextUp, KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation15() {
  const { lesson, course, accent } = findLesson("piano-foundation", "15");

  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-foundation/PF15-Hand-Ratios-Animated.mp4"
        caption={`LH:RH note-count ratios — 1:1, 1:2, 1:4, 1:8 — the same progression at four textures`}
        hint={`≈ 22 sec · the texture dial`}
      />
      <KeyHighlights>
        <Highlight>Four chords from the G major scale: <strong>Em (B-E-G with E bass)</strong>, <strong>C (C-E-G with C bass)</strong>, <strong>G (B-D-G with G bass)</strong>, <strong>D (A-D-F♯ with D bass)</strong>.</Highlight>
        <Highlight>Right hand uses <strong>thumb · middle · pinky</strong> on every chord shape. The thumb just slides up by one note as you move from Em → C, the rest follows.</Highlight>
        <Highlight>Hand Ratio is the relationship between left and right — and it works <strong>both ways</strong>: 1:4 (right pulses more) AND 4:1 (left pulses more). Plus mixes like 2:4 and 4:2.</Highlight>
        <Highlight>Both hands always start on beat 1 — <em>"The two hands collide at the 1 and then they go their separate ways."</em></Highlight>
        <Highlight>Practise every ratio <strong>away from the piano first</strong> — on your body, on a tabletop. Once the body owns the ratio, the piano just plays it.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Setting up the four chords" title="One scale, four chords, one fingering" accent={accent.hex}>
        <p>
          Everything in this lesson sits on the <strong>G major scale</strong>. The four chords
          are the ones you'll reuse across countless songs:
        </p>
        <ul className="space-y-1 text-[14px]">
          <li>Chord 1 — <strong>Em</strong>: right hand B-E-G, left hand E in the bass.</li>
          <li>Chord 2 — <strong>C</strong>: right hand C-E-G, left hand C in the bass.</li>
          <li>Chord 3 — <strong>G</strong>: right hand B-D-G, left hand G in the bass.</li>
          <li>Chord 4 — <strong>D</strong>: right hand A-D-F♯, left hand D in the bass.</li>
        </ul>
        <p>
          Right-hand fingering is identical for every chord — <strong>thumb, middle, pinky</strong>.
          Most of the work is the thumb sliding to the next root; the other two fingers just
          come along. Left hand grabs the bass note (octave doubling sounds richer if your
          hand stretches; a single note is fine if not).
        </p>

        <KeyIdea>
          These four chords aren't random. They're all built from the G major scale, so once
          you know them as scale-degree numbers (vi · IV · I · V), you can transpose the same
          loop into any other key without learning new shapes.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The big idea" title="Rhythm makes the chords speak" accent={accent.hex}>
        <p>
          Knowing the chords is half the job. Once you have them, the rhythm pattern you choose
          to play them in is what makes the music breathe. The rhythmic piece is <strong>Hand Ratios</strong>: how the left hand and the right hand
          interact with each other across a bar.
        </p>

        <Pull>
          The two hands collide at the 1 and then they go their separate ways.
        </Pull>

        <p>
          That single rule explains the whole picture. The two hands always meet on the
          downbeat. From there, they can split apart in any of several ways: right hand
          pulses four times under one left bass (1:4); right hand once, left hand pulses four
          (4:1); both active in mixed counts (2:4, 4:2). Each combination has its own feel.
        </p>
      </Insight>

      <Insight eyebrow="The ratio dial" title="Eight bidirectional ratios, all on Em" accent={accent.hex}>
        <p>
          The picture below is one bar of music split into eight sixteenth-note cells.
          Blue dots are <strong>left-hand bass</strong>; orange dots are <strong>right-hand
          chord</strong>. They always meet on beat 1. Walk through the rows and notice how
          flipping the ratio (1:4 → 4:1) completely changes the feel even though the chord
          stays the same.
        </p>

        <HandRatioMeter />

        <p className="text-sm">
          Once each row is in your hands, drop it onto the four-chord loop: Em → C → G → D.
          Same rhythm pattern, just stepping through the four chords one bar at a time.
        </p>
      </Insight>

      <Insight eyebrow="Ratio choice in practice" title="Which one when" accent={accent.hex}>
        <p>
          The "right" ratio depends on three things — and all three are called out explicitly:
        </p>
        <ol className="space-y-2 text-[14px]">
          <li>
            <strong>The energy the song demands.</strong> A quiet verse wants something sparse
            (1:2 or 1:3); a chorus wants something denser (1:4 or 2:4); a slow ballad might
            sit on 1:1 with both hands together.
          </li>
          <li>
            <strong>The genre.</strong> Reggae loves 4:1 — a walking left hand with a single
            chord stamp on the off-beat. Pop and gospel sit at 1:4. A 3/4 waltz fits 1:3
            (right hand pulses three times across the bar).
          </li>
          <li>
            <strong>What the band is doing.</strong> If a guitarist is strumming heavily,
            you go sparse. If the drummer is laying down a busy hi-hat, you let your left hand
            answer and keep the right hand rare. The ratio is your conversation with the band.
          </li>
        </ol>
      </Insight>

      <Insight eyebrow="Practice path" title="Body first, then chords, then sing" accent={accent.hex}>
        <Steps>
          <Step>
            <strong>Tap the ratio on your body.</strong> Left thigh = left hand, right thigh =
            right hand. Start with 1:4. Both hands hit on beat 1; right hand keeps tapping
            three more sixteenths. Loop until it feels obvious.
          </Step>
          <Step>
            <strong>Same ratio, one chord.</strong> Sit down. Left hand on E (or E octave) for
            the bass. Right hand on B-E-G for the Em chord. Play 1:4 — left once, right four
            times. Loop a full minute on Em.
          </Step>
          <Step>
            <strong>Walk the four chords.</strong> Same 1:4 ratio, but now move chord every
            bar: Em → C → G → D → back. The thumb slides; middle and pinky come along.
          </Step>
          <Step>
            <strong>Flip the ratio.</strong> Same four chords, now 4:1 — left hand walking,
            right chord landing once per bar. You're now in reggae territory without changing
            a single note.
          </Step>
          <Step>
            <strong>Sing on top.</strong> Pick a song you know in G that fits the four-chord
            cycle. Hold your favourite ratio underneath while you sing the melody. If the
            ratio chokes the vocal, drop a level. If it feels limp, climb a level.
          </Step>
        </Steps>

        <TryThis duration="3 min" title="The body-only ratio drill">
          Pick any three ratios — say 1:4, 4:1, and 2:4. Cycle them on your body without an
          instrument: 4 bars of each, no break. The hands should be confident on every
          combination before you ever touch the keyboard. This drill is taught deliberately: <em>"You don't have to actually sit here and do the job on the
          piano. You can grow as a pianist literally anywhere."</em>
        </TryThis>

        <Mistake>
          Don't lock the left hand into a single octave-jump style. If your hand can't reach
          the octave comfortably, just play the single bass note. A clean single note in time
          beats a strained octave that drags behind the beat every time.
        </Mistake>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-16"
        label="Lesson 16 · Basic Arpeggio Patterns"
        why="Hand Ratios give you density. The next lesson breaks each chord into separate notes — that's the arpeggio — and stacks new patterns on top of the same four chords."
      />
    </LessonShell>
  );
}
